/**
 * macOS WKWebView scroll-delta inversion fix
 *
 * WKWebView on macOS reports scroll-delta inverted compared to standard browsers.
 * When dragging down (positive deltaY), WKWebView reports negative scroll values,
 * causing DnD Kit's autoScroll to scroll in the opposite direction.
 *
 * This module provides a wrapper that detects macOS and inverts scroll-delta
 * before it reaches DnD Kit's autoScroll mechanism.
 */

import { Capacitor } from "@capacitor/core"

/**
 * Check if running on macOS Capacitor (desktop app)
 */
export function isMacOSWebView(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "macOS"
}

/**
 * Monkey-patch Element.scrollTop setter to invert scroll on macOS.
 *
 * When DnD Kit's autoScroll calls `element.scrollTop += delta`, on macOS
 * we need to invert the delta so the direction is correct.
 *
 * Call this once at app startup (e.g. in main.tsx or App root).
 */
export function patchScrollBehaviorForMacOS(): void {
  if (!isMacOSWebView()) return

  // Store original descriptor
  const originalDescriptor = Object.getOwnPropertyDescriptor(
    Element.prototype,
    "scrollTop",
  )
  if (!originalDescriptor) return

  Object.defineProperty(Element.prototype, "scrollTop", {
    get(this: Element) {
      return originalDescriptor.get!.call(this)
    },
    set(this: Element, value: number) {
      // Only apply inversion during DnD (when we're trying to scroll)
      // Check if we're in a drag context by looking for active DnD elements
      const isDragging = document.body.classList.contains("dragging") ||
        document.querySelector("[data-dnd-active]") !== null ||
        document.querySelector("[role='button'][aria-pressed='true']") !== null

      if (isDragging) {
        // Calculate delta from current position
        const current = originalDescriptor.get!.call(this)
        const delta = value - current
        // Invert the delta: if dragging down and delta is positive, make it negative
        const invertedValue = current - delta
        originalDescriptor.set!.call(this, invertedValue)
      } else {
        // Normal scroll (not during drag)
        originalDescriptor.set!.call(this, value)
      }
    },
    configurable: true,
  })
}

/**
 * Alternative: Patch Element.scrollBy to invert scroll on macOS.
 * This catches element scrolling during DnD and inverts the direction.
 */
export function patchWindowScrollForMacOS(): void {
  if (!isMacOSWebView()) return

  const originalScrollBy = Element.prototype.scrollBy as any

  ;(Element.prototype.scrollBy as any) = function (this: Element, x: any, y?: any): void {
    // Detect if we're in a drag context
    const isDragging = document.body.classList.contains("dragging") ||
      document.querySelector("[data-dnd-active]") !== null

    if (!isDragging) {
      // Normal scroll — don't patch
      if (y !== undefined) {
        originalScrollBy.call(this, x, y)
      } else {
        originalScrollBy.call(this, x)
      }
      return
    }

    // During drag — invert direction
    if (typeof x === "number" && typeof y === "number") {
      originalScrollBy.call(this, x, -y)
    } else if (typeof x === "number") {
      originalScrollBy.call(this, x, y)
    } else {
      // ScrollToOptions
      const opts: ScrollToOptions = { ...x }
      if (opts.top !== undefined) opts.top = -opts.top
      if (opts.left !== undefined) opts.left = -opts.left
      originalScrollBy.call(this, opts)
    }
  }
}
